/**
 * Training Methods
 * API methods for model training and pipeline execution
 */

import { ModelParameters } from '../../../components/ModelConfiguration';
import { TrainingDataSplitData } from '../../../components/TrainingDataSplit';
import { ValidationService } from '../../../../../core/services/validation/validationService';
import { convertUIToBackendFormat } from '../../../../../utils/modelParameterUtils';
import { makeRequest, getApiBaseUrl } from '../core';
import { TrainingApiResponse } from '../types';

/**
 * Train models with proper parameter validation
 */
export async function trainModels(
  sessionId: string,
  modelConfiguration: ModelParameters,
  trainingDataSplit?: TrainingDataSplitData
): Promise<TrainingApiResponse> {
  // NOTE: Backend expects UI format parameters (not converted)
  // Backend will handle conversion internally
  const requestBody: Record<string, unknown> = {
    model_parameters: modelConfiguration,
    training_split: trainingDataSplit
  };

  return makeRequest(`${getApiBaseUrl()}/train-models/${sessionId}`, {
    method: 'POST',
    body: JSON.stringify(requestBody)
  });
}

/**
 * Start complete pipeline with validation
 */
export async function startCompletePipeline(
  sessionId: string,
  modelConfiguration: ModelParameters,
  trainingDataSplit: TrainingDataSplitData
): Promise<TrainingApiResponse> {
  // Validate all parameters
  const formValidation = ValidationService.validateCompleteForm({
    inputFiles: [], // Will be validated separately
    outputFiles: [], // Will be validated separately
    timeInfo: { session_id: sessionId, jahr: false, woche: false, monat: false, feiertag: false, tag: false, zeitzone: '', category_data: {} },
    zeitschritte: { eingabe: '', ausgabe: '', zeitschrittweite: '', offset: '' },
    modelConfiguration,
    trainingDataSplit,
    totalDataPoints: 0
  });

  if (!formValidation.isValid) {
    const errors = formValidation.errors.map((e: { message: string }) => e.message).join(', ');
    throw new Error(`Form validation failed: ${errors}`);
  }

  // Convert parameters
  const backendModelParams = convertUIToBackendFormat(modelConfiguration);
  const backendSplitParams = {
    train_ratio: trainingDataSplit.trainPercentage / 100,
    validation_ratio: trainingDataSplit.valPercentage / 100,
    test_ratio: trainingDataSplit.testPercentage / 100,
    shuffle: !trainingDataSplit.random_dat,
    random_state: 42,
    stratify: false
  };

  const requestBody = {
    model_parameters: backendModelParams,
    training_split: backendSplitParams
  };

  return makeRequest(`${getApiBaseUrl()}/start-complete-pipeline/${sessionId}`, {
    method: 'POST',
    body: JSON.stringify(requestBody)
  });
}
